import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import { upperFirst, uniqBy } from "lodash-es";

import {Page} from "../flipp-page/Page";
import {FlyerListingItem, FlyerListingItemContext} from "../flipp-flyer-listing-item/FlyerListingItem";
import {LoadingSpinner} from "../flipp-loading/LoadingSpinner";

import {inject} from "../../services/di";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {StorageService} from "../../services/StorageService";
import {ConfigService} from "../../services/ConfigService";
import {FlyerPlacement} from "../../services/definitions/IAnalyticsService";
import {UserAgentService} from "../../services/UserAgentService";
import FlyersSortService from "../../services/FlyersSortService";
import MerchantService from "../../services/MerchantService";
import renderBreadcrumbs from "../renderBreadcrumbs";
import MetadataService from "../../services/MetadataService";
import BreadcrumbsService from "../../services/BreadcrumbsService";
import {SeoDataService} from "../../services/SeoDataService";
import {slugify} from "../../services/LinkService";
import { SeoService } from "../../services/SeoService";

interface StorePageAttributes {
	withSidebar?: boolean;
	store?: string;
	merchants?: any;
}

@element("flipp-store-page")
export class StorePage extends Component<StorePageAttributes, any> {
	@attr store: string = "";
	@attr withSidebar: boolean = true;
	@attr merchants: string;

	refs: {
		title: HTMLElement,
		content: HTMLElement,
		page: HTMLElement
	};

	state = {
		scrollTop: 0
	};

	render() {
		return <Page ref="page" show="flyers" onScroll={this._handleScroll}>
			<StorePageContent store={this.props.store} merchants={this.props.merchants} />
		</Page>;
	}

	onRender() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	onAttach() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	private _handleScroll = (e: Event) => {
		this.state.scrollTop = this.refs.page.scrollTop;
	}

}

interface StorePageContentAttributes {
	store?: string;
	category?: string;
	sort?: string;
	merchants?: string;
}

@element("flipp-store-page-content")
export class StorePageContent extends Component<StorePageContentAttributes, Flyer[][]> {

	@inject location: LocationService;
	@inject flyerService: FlyerService;
	@inject analytics: AnalyticsService;
	@inject storage: StorageService;
	@inject config: ConfigService;
	@inject userAgent: UserAgentService;
	@inject flyersSortService: FlyersSortService;
	@inject merchantService: MerchantService;
	@inject metadataService: MetadataService;
	@inject breadcrumbsService: BreadcrumbsService;
	@inject seoDataService: SeoDataService;
	@inject seoService: SeoService;

	@attr category: string = "All Flyers";
	@attr sort: string = "featured";
	@attr store: string = "";
	@attr merchants: string;

	// @ts-ignore
	prepare() {
		const topCities = this.seoDataService.getTopCities().sort().slice(0, 5);
		const topStatesCodes = topCities.map((e: any) => e.postalCode);
		const promises = topStatesCodes.map((code: any) => this.flyerService.getFlyers(code));

		return Promise.all([...promises]);
	}

	renderLoading() {
		return <LoadingSpinner />;
	}

	render(responses: any) {
		let flyers: any = [];
		for (const r of responses) {
			flyers = [...flyers, ...r];
		}
		this.flyerService.setStoreFlyers(flyers);
		flyers = flyers.filter((flyer: any) => slugify(flyer.merchant) === slugify(this.props.store));
		const uniqFlyers = uniqBy(flyers, (e: Flyer) => e.id);
		const { premiumFlyers, normalFlyers } =
			this.flyersSortService.filter(uniqFlyers, this.category, this.sort);


		this.seoService.insertLoadingMarker(70);
		return <div>
			<h1>{this.getH1()}</h1>
			<div className="breadcrumbs">
				{/*{this.getBreadcrumbs(this.props.store)}*/}
			</div>
			<h2>{this.getH2()}</h2>
			{this.getStoresDescription(this.props.merchants)}
			<div class="content">
				<div>
					{ premiumFlyers.map((flyer, idx) =>
						<FlyerListingItem flyerId={flyer.id} context={FlyerListingItemContext.Listing} premium
							category={this.category}
							onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Promoted)} />) }
				</div>
				<div>
					{ normalFlyers.map((flyer, idx) =>
						<FlyerListingItem flyerId={flyer.id} context={FlyerListingItemContext.Listing}
							category={this.category}
							onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Organic)} />) }
				</div>

				{ premiumFlyers.length === 0 && normalFlyers.length === 0 ?
					<div class="empty-item-container">
						<div>{i18n("no_flyers_for_store", {
							store: this.props.store
						})}</div>
					</div>
					: null }
			</div>
		</div>;
	}


	private getH1 = () => {
		return i18n("store_flyers_h1", {
			store: upperFirst(this.store)
		});
	}

	private getH2 = () => {
		return i18n("store_flyers_h2", {
			store: this.props.store
		});
	}

	private getBreadcrumbs = (merchant: any) => {
		const locale = this.metadataService.getLocale();
		const breadcrumbs = [
			{ path: locale, name: "Home" },
			{ path: `${locale}/${merchant}`, name: merchant }
		];

		return renderBreadcrumbs(breadcrumbs, this.breadcrumbsService.getCurrentLocation());
	}

	private getStoresDescription = (merchants: any) => {
		return JSON.parse(merchants).map((m: any) => {
			return <p>Address: {m.address}, {m.city}, {m.province}, {m.postal_code}</p>;
		});
	}


	private _handleFlyerClick = (idx: number, placement: FlyerPlacement, e: Event) => {
		this.analytics.trackFlyerClick(this.category, this.sort, placement, idx);
	}
}
